import { LoginMethodType } from '@/core/data/auth/auth.type';
import type { BaseGambitDto, GambitSparkState } from '@/core/data/gambit/gambit.interface';
import type { HandshakeDto } from '@/core/data/handshake/handshake.interface';
import type { UserRatings } from '@/core/data/rating/rating.interface';
import type { RatingType } from '@/core/data/rating/rating.type';
import {
  PlayerStatus,
  PlayerTitle,
  type PlayerType,
  type RatingDisplayMode,
  type SignUpProvider,
  type UsernameIssue,
  type UsernameRejection,
} from '@/core/data/user/user.type';
import { UnitSystem } from '@/views/components/gam-player/helpers';
import type { CityDto, GeoCoords } from '../location/location.interface';

export interface PersonalUserDto {
  id: string;
  firstName?: string;
  lastName?: string;
  username: string;
  /** Set where a record outlives the account it points at, such as a blocked player who has since deleted theirs. */
  isDeleted?: boolean;
  status?: PlayerStatus;
  emoji?: string;
  age?: number;
  ratings?: PlayerRatingsDto[];
  rating?: number;
  ratingDisplayMode?: RatingDisplayMode;
  playerTypes?: PlayerType[];
  isGambitsMaster?: boolean;
  title?: PlayerTitle;
  profileImageBlobHash: string | null;
  chessBoardImageBlobHash?: string;
  country?: string;
  city?: CityDto;
}

export interface AdminUserDto extends PersonalUserDto {
  createdAt: string;
  age: number;
  email?: string;
  loginProvider?: SignUpProvider | null;
  lastActiveAt?: string | null;
}

export interface UserDto extends BaseUserInfo {
  uid: string;
  age: number;
  mutual: number;
  reliability: string;
  friends: number;
  mutualFriends: number;
  players: number;
  mutualPlayers: number;
  gambitsCreated: number;
  gambitsWon: number;
  gambitsDraw: number;
  gambitsPlayed: number;
  gambitsNextMove: number;
  gambitsAttended: number;
  visitedLocations: number;
  likes: number;
  upcomingGambits: BaseGambitDto[];
  handshakeCount?: number;
  handshakeRank?: number | null;
  handshakes: HandshakeDto[];
  followers: number;
  following: number;
  clubsFollowing: number;
  isFollowed: boolean;
  isFollowingMe: boolean;
  isBlocked: boolean;
  createdAt: string;
  featured?: boolean;
}

export interface PlayerRatingsDto {
  type: RatingType;
  rating?: number | null;
}

export interface GetUsersOptions {
  /** Explicit anchor for a nearby request. Falls back to the caller's stored location. */
  location?: GeoCoords | null;
  /** Limit results to players around the anchor. Ignored while searching. */
  nearby?: boolean;
}

export interface BaseUserDto extends PersonalUserDto {
  birthDate?: string;
  status?: PlayerStatus;
  distance?: number;
  featured?: boolean;
  phone?: string;
  /** You follow them. */
  isFollowed?: boolean | null;
  /** They follow you. */
  isFollowingMe?: boolean | null;
}

export interface HandshakeFields {
  handshakeCount: number | null;
  isHandshakeConfirmed: boolean | null;
  isHandshakePending: boolean | null;
  pendingHandshakes: HandshakeDto[] | null;
}

export interface AttendeeDto extends BaseUserDto {
  pseudonym?: string;
  chessSetCount?: number;
  isOrganizer?: boolean;
  isBlocked?: boolean;
  handshakeCount?: number;
  sparkNotification?: GambitSparkState;
  isConfirmed?: boolean;
  isCheckedIn?: boolean;
  // checkOutTime?: string | null;
}

export interface InviteeDto {
  id: string;
  firstName: string;
  lastName: string;
  username: string;
  emoji: string;
  rating: number;
  ratingDisplayMode?: RatingDisplayMode;
  profileImageBlobHash?: string | null;
  isBlocked?: boolean;
}

/**
 * Resolves who owns a handle, nothing more. The endpoint is public, so it only
 * returns an identifier — do not widen this to a full user or club DTO.
 */
export interface UsernameOwnerDto {
  id: string;
  username: string;
}

export interface GetUsernameDto {
  user?: UsernameOwnerDto | null;
  club?: UsernameOwnerDto | null;
  error?: string;
}

export interface ValidateUserDto {
  isUsernameValid: boolean;
  reason?: UsernameRejection;
}

export interface AdminUsernameIssueDto {
  id: string;
  username: string;
  email: string;
  createdAt: string;
  issue: UsernameIssue;
}

export interface ClubOptionFields {
  id: string;
  name: string;
  username: string;
  profileImageBlobHash: string;
}

export interface BaseUserInfo extends PersonalUserDto, UserRatings {
  email: string;
  birthDate: string;
  aboutMe: string;
  provider: LoginMethodType;
  location: GeoCoords;
  city?: CityDto;
  // cityId?: string | null;
  isPayingUser?: boolean;
  isAdmin?: boolean;
  preferredPlayingTime: string | null;
  clubs?: ClubOptionFields[];
  organizedClubs?: ClubOptionFields[];
  /** @deprecated Use notificationPreferences instead */
  enabledNotifications?: string[];
  notificationPreferences?: NotificationPreferences;
  blocked: PersonalUserDto[];
  unitType: UnitSystem | null;
  accentHue?: number | null;
  /** Newest custom chess-clock controls. Null until one has been saved. */
  recentClockControls?: { minutes: number; increment: number }[] | null;
  phone?: string;
  handshakePartnerIds?: string[];
  /** Players a position was played with. A handshake with no position is left out. */
  positionPartnerIds?: string[];
}

export enum NotificationPreferenceType {
  MUTED = 'muted',
  PUSH = 'push',

  // Nearby check-ins
  CHECK_IN_NEARBY_RECOMMENDED_ELO = 'check_in.nearby.recommended_elo',
  CHECK_IN_NEARBY_FOLLOWED_PLAYER = 'check_in.nearby.followed_player',
  CHECK_IN_NEARBY_FOLLOWED_LOCATION = 'check_in.nearby.followed_location',

  // Nearby gambits
  GAMBIT_NEARBY_RECOMMENDED_ELO = 'gambit.nearby.recommended_elo',
  GAMBIT_NEARBY_FOLLOWED_PLAYER = 'gambit.nearby.followed_player',
  GAMBIT_NEARBY_FOLLOWED_CLUB = 'gambit.nearby.followed_club',
  GAMBIT_NEARBY_FOLLOWED_LOCATION = 'gambit.nearby.followed_location',

  // Attended gambits
  GAMBIT_ATTENDEE_CHANGED = 'gambit.attendee.details_changed',
  GAMBIT_ATTENDEE_CANCELED = 'gambit.attendee.gambit_canceled',
  GAMBIT_ATTENDEE_JOINED = 'gambit.attendee.player_joined',
  GAMBIT_ATTENDEE_LEFT = 'gambit.attendee.player_left',
  GAMBIT_ATTENDEE_EVERYONE_LEFT = 'gambit.attendee.everyone_left',
  GAMBIT_ATTENDEE_ORGANIZER_LEFT = 'gambit.attendee.organizer_left',
  GAMBIT_ATTENDEE_CHAT = 'gambit.attendee.chat',

  // Owned gambits
  GAMBIT_OWNER_JOINED = 'gambit.owner.player_joined',
  GAMBIT_OWNER_LEFT = 'gambit.owner.player_left',
  GAMBIT_OWNER_EVERYONE_LEFT = 'gambit.owner.everyone_left',

  // Co-organized gambits
  GAMBIT_ORGANIZER_CHANGED = 'gambit.organizer.details_changed',
  GAMBIT_ORGANIZER_CANCELED = 'gambit.organizer.gambit_canceled',
  GAMBIT_ORGANIZER_JOINED = 'gambit.organizer.player_joined',
  GAMBIT_ORGANIZER_LEFT = 'gambit.organizer.player_left',
  GAMBIT_ORGANIZER_EVERYONE_LEFT = 'gambit.organizer.everyone_left',

  // Challenges
  GAMBIT_CHALLENGE_CREATED = 'gambit.challenge.created',
  GAMBIT_CHALLENGE_ACCEPTED = 'gambit.challenge.accepted',
  GAMBIT_CHALLENGE_DECLINED = 'gambit.challenge.declined',
  GAMBIT_CHALLENGE_CANCELED = 'gambit.challenge.canceled',
  GAMBIT_CHALLENGE_CHANGED = 'gambit.challenge.changed',

  // Player activity
  USER_FOLLOWING = 'user.following',
  USER_LIKE = 'user.like',

  // Chat activity
  // MESSAGE_CREATED = 'message.created',

  // Sparks
  SPARK_MATCHED = 'spark.matched',
  SPARK_CONFIRMED = 'spark.confirmed',
  SPARK_BOOKED = 'spark.booked',
  SPARK_ACTIVITY = 'spark.activity',
  SPARK_COMPROMISED = 'spark.compromised',
  SPARK_PROPOSAL_PLAYER_VOTED = 'spark.proposal.player_voted',

  // Boards
  BOARD_MOVE_ON_POSITION = 'board.move_on_position',
  BOARD_MOVE_ON_PLANTED = 'board.move_on_planted',
  BOARD_COMPLETED = 'board.completed',

  // Handshakes
  HANDSHAKE_PASSERBY_CLAIMED = 'handshake.passerby_claimed',

  // SMS activity
  SMS_SPARK_ACTIVITY = 'sms.spark_activity',

  // Email preferences
  EMAIL_SPARK_ACTIVITY = 'email.spark_activity',
  EMAIL_DAILY_NEWS = 'email.daily_news',

  // System
  WELCOME = 'system.welcome',
}

// New hierarchical notification preferences
export enum NotificationCategory {
  IRL_PLAY_CONFIRMED = 'irl_play_confirmed',
  IRL_PLAY_CHANGED = 'irl_play_changed',
  IRL_PLAY_PROPOSED = 'irl_play_proposed',
  MEET_ANYWAYS = 'meet_anyways',
  CHALLENGES = 'challenges',
  INVITATIONS = 'invitations',
  CHECK_INS = 'check_ins',
  CHAT = 'chat',
  NEW_FOLLOWERS = 'new_followers',
  BOARDS = 'boards',
  HANDSHAKES = 'handshakes',
}

export enum NotificationSubType {
  // IRL play confirmed
  WHERE_I_PLAY = 'where_i_play',
  FOLLOWED_PLAYER = 'followed_player',
  SIMILAR_EXPERIENCE = 'similar_experience',
  FOLLOWED_SPOT = 'followed_spot',
  // IRL play proposed
  WHERE_I_PLAY_PROPOSED = 'where_i_play_proposed',
  WHERE_I_AM_AVAILABLE = 'where_i_am_available',
  // IRL play changed
  TIME_OR_SPOT_CHANGED = 'time_or_spot_changed',
  COMPROMISED = 'compromised',
  CANCELED = 'canceled',
  // Challenges
  CHALLENGE_RECEIVED = 'challenge_received',
  CHALLENGE_ACCEPTED = 'challenge_accepted',
  CHALLENGE_UPDATED = 'challenge_updated',
  CHALLENGE_DECLINED = 'challenge_declined',
  CHALLENGE_WITHDRAWN = 'challenge_withdrawn',
  // Invitations
  INVITATION_RECEIVED = 'invitation_received',
  INVITATION_ACCEPTED = 'invitation_accepted',
  INVITATION_DECLINED = 'invitation_declined',
  // Boards
  MOVE_ON_MY_POSITION = 'move_on_my_position',
  MOVE_ON_PLANTED_BOARD = 'move_on_planted_board',
  BOARD_COMPLETED = 'board_completed',
}

export enum EmailCategory {
  SPARKS = 'sparks',
  CHALLENGES = 'challenges',
  INVITATIONS = 'invitations',
  CHAT = 'chat',
  DAILY = 'daily',
}

export enum SmsCategory {
  SPARKS = 'sparks',
}

export interface CategoryPreference {
  enabled: boolean;
  mode: 'all' | 'only';
  subTypes: string[];
}

/** `push` omitted means push delivery is off (no matrix stored); merge with defaults only in the UI. */
export interface NotificationPreferences {
  push?: Record<NotificationCategory, CategoryPreference>;
  email: Record<EmailCategory, boolean>;
  sms: Record<SmsCategory, boolean>;
}

/** Default push matrix; merge with `userDetails.notificationPreferences?.push` for partial API payloads. */
export function getDefaultEmailPreferences(): Record<EmailCategory, boolean> {
  return {
    [EmailCategory.SPARKS]: true,
    [EmailCategory.CHALLENGES]: true,
    [EmailCategory.INVITATIONS]: true,
    [EmailCategory.CHAT]: false,
    [EmailCategory.DAILY]: true,
  };
}

export function getDefaultPushPreferences(): Record<NotificationCategory, CategoryPreference> {
  return {
    [NotificationCategory.IRL_PLAY_CONFIRMED]: {
      enabled: true,
      mode: 'only',
      subTypes: [NotificationSubType.WHERE_I_PLAY],
    },
    [NotificationCategory.IRL_PLAY_CHANGED]: { enabled: true, mode: 'all', subTypes: [] },
    [NotificationCategory.IRL_PLAY_PROPOSED]: {
      enabled: true,
      mode: 'only',
      subTypes: [NotificationSubType.WHERE_I_PLAY_PROPOSED],
    },
    [NotificationCategory.MEET_ANYWAYS]: { enabled: true, mode: 'all', subTypes: [] },
    [NotificationCategory.CHALLENGES]: { enabled: true, mode: 'all', subTypes: [] },
    [NotificationCategory.INVITATIONS]: { enabled: true, mode: 'all', subTypes: [] },
    [NotificationCategory.CHECK_INS]: {
      enabled: false,
      mode: 'only',
      subTypes: [],
    },
    [NotificationCategory.CHAT]: { enabled: true, mode: 'all', subTypes: [] },
    [NotificationCategory.NEW_FOLLOWERS]: { enabled: true, mode: 'all', subTypes: [] },
    [NotificationCategory.BOARDS]: { enabled: true, mode: 'all', subTypes: [] },
    [NotificationCategory.HANDSHAKES]: { enabled: true, mode: 'all', subTypes: [] },
  };
}

export interface ProfileRatingsUpdatePayload extends UserRatings {
  firstName: string;
  lastName: string;
  username: string;
  birthDate: string;
  ratingDisplayMode?: RatingDisplayMode;
}

export enum AppEvent {
  USER_CREATED = 'user.created',
  USER_REPORTED = 'user.reported',
  USER_FOLLOWING = 'user.following',
  USER_LIKE = 'user.like',
  CLUB_FOLLOWING = 'club.following',

  GAMBIT_CREATED = 'gambit.created',
  GAMBIT_TERMINATED = 'gambit.terminated',
  GAMBIT_UPDATED = 'gambit.updated',
  GAMBIT_DELETED = 'gambit.deleted',
  GAMBIT_COMPLETED = 'gambit.completed',

  GAMBIT_JOINED = 'gambit.joined',
  GAMBIT_LEFT = 'gambit.left',
  GAMBIT_CHAT = 'gambit.chat',

  SPARK_CREATED = 'spark.created',
  SPARK_JOINED = 'spark.joined',
  SPARK_LEFT = 'spark.left',

  SPARK_MATCHED = 'spark.matched',
  SPARK_CONFIRMED = 'spark.confirmed',
  SPARK_BOOKED = 'spark.booked',
  SPARK_COMPROMISED = 'spark.compromised',

  LOCATION_JOINED = 'location.joined',
  LOCATION_LEFT = 'location.left',

  GAMBIT_RESULT_CREATED = 'gambit.result.created',
  GAMBIT_RESULT_UPDATED = 'gambit.result.updated',

  GAMBIT_INVITATION_CREATED = 'gambit.invitation.created',
  GAMBIT_INVITATION_WITHDRAWN = 'gambit.invitation.withdrawn',
  GAMBIT_INVITATION_ACCEPTED = 'gambit.invitation.accepted',
  GAMBIT_INVITATION_DECLINED = 'gambit.invitation.declined',

  GAMBIT_CHALLENGE_CREATED = 'gambit.challenge.created',
  GAMBIT_CHALLENGE_ACCEPTED = 'gambit.challenge.accepted',
  GAMBIT_CHALLENGE_CHANGED = 'gambit.challenge.changed',
  GAMBIT_CHALLENGE_DECLINED = 'gambit.challenge.declined',
  GAMBIT_CHALLENGE_CANCELED = 'gambit.challenge.canceled',

  GAMBIT_SUGGESTION_CREATED = 'gambit.suggestion.created',
  GAMBIT_SUGGESTION_ACCEPTED = 'gambit.suggestion.accepted',

  CHAT_CREATED = 'chat.created',

  MESSAGE_CREATED = 'message.created',
  MESSAGE_READ = 'message.read',

  CHAT_TERMINATED = 'chat.terminated',
  FEED_CREATED = 'feed.created',
  FEED_SHARED = 'feed.shared',
  FEED_LIKE = 'feed.like',

  LOCATION_CREATED = 'location.created',

  NOTIFICATION_CREATED = 'notification.created',
}
